import { useTranslation } from "react-i18next";
import * as Popover from "../../Embedding/Popover";
import { IconChevronDown, IconKeyboard } from "../../icons";
import * as React from "react";

import { AttachmentButton } from "./AttachmentButton";
import { BlockquoteButton } from "./BlockquoteButton";
import { BoldButton } from "./BoldButton";
import { BulletListButton } from "./BulletListButton";
import { HeadingButtons } from "./HeadingButtons";
import { ItalicButton } from "./ItalicButton";
import { LinkButton } from "./LinkButton";
import { NumberListButton } from "./NumberListButton";
import { TaskListButton } from "./TaskListButton";
import { TableButton } from "./TableButton";
import { RedoButton } from "./RedoButton";
import { StrikeButton } from "./StrikeButton";
import { UndoButton } from "./UndoButton";

import { useWindowSizeBreakpoints } from "../../utils/useWindowSizeBreakpoint";
import { useTipTapEditor, useUploadFile } from "../EditorContext";
import { LinkEditForm } from "../LinkEditForm";
import { CodeBlockButton } from "./CodeBlockButton";
import { ColorPicker } from "./ColorPicker";
import { DividerButton } from "./DividerButton";
import { MarkdownHelp, MarkdownHelpContent, markdownHelpPopoverClassName } from "./MarkdownHelp";
import { ToolbarButton } from "./ToolbarButton";

export function Toolbar({ compact }: { compact?: boolean } = {}): JSX.Element {
  const size = useWindowSizeBreakpoints();

  if (compact || size == "xs") {
    return <MobileToolbar />;
  } else {
    return <DesktopToolbar />;
  }
}

function DesktopToolbar() {
  const editor = useTipTapEditor();
  const uploadFile = useUploadFile();
  const border = "border-b";

  return (
    <div className="sticky bg-surface-base z-10 rounded-t-lg top-0">
      <div className={"flex items-center gap-3 border-stroke-base" + " " + border}>
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center">
            <BoldButton editor={editor} iconSize={20} />
            <ItalicButton editor={editor} iconSize={20} />
            <StrikeButton editor={editor} iconSize={20} />

            <Separator />

            <HeadingButtons editor={editor} iconSize={20} />

            <Separator />
            <ColorPicker editor={editor} iconSize={18} />
            <Separator />

            <BulletListButton editor={editor} iconSize={20} />
            <NumberListButton editor={editor} iconSize={20} />
            <TaskListButton editor={editor} iconSize={20} />
            <TableButton editor={editor} iconSize={20} />
            <BlockquoteButton editor={editor} iconSize={20} />
            <CodeBlockButton editor={editor} iconSize={20} />
            <DividerButton editor={editor} iconSize={20} />

            <Separator />
            <LinkButton editor={editor} iconSize={20} />
            <Separator />
            <MarkdownHelp />
          </div>

          <div className="flex items-center gap-1">
            {uploadFile ? (
              <>
                <AttachmentButton editor={editor} iconSize={20} />
                <Separator />
              </>
            ) : null}
            <UndoButton editor={editor} iconSize={20} />
            <RedoButton editor={editor} iconSize={20} />
          </div>
        </div>
      </div>

      <LinkEditForm editor={editor} />
    </div>
  );
}

function MobileToolbar() {
  const border = "border-y";
  const editor = useTipTapEditor();
  const uploadFile = useUploadFile();

  return (
    <div className="sticky bg-surface-base z-10 rounded-t-lg top-0">
      <div className={"flex items-center gap-3 border-stroke-base" + " " + border}>
        <div className="flex justify-between items-center w-full min-w-0">
          <div className="flex items-center min-w-0 overflow-x-auto">
            <BoldButton editor={editor} iconSize={20} />
            <ItalicButton editor={editor} iconSize={20} />
            <StrikeButton editor={editor} iconSize={20} />
            <HeadingButtons editor={editor} iconSize={20} />
            <ColorPicker editor={editor} iconSize={18} />
            <BulletListButton editor={editor} iconSize={20} />
            {uploadFile ? <AttachmentButton editor={editor} iconSize={20} /> : null}
          </div>

          <div className="shrink-0">
            <MobilePopupTools>
              <TableButton editor={editor} iconSize={20} tabIndex={0} />
              <TaskListButton editor={editor} iconSize={20} />
              <NumberListButton editor={editor} iconSize={20} />
              <BlockquoteButton editor={editor} iconSize={20} />
              <CodeBlockButton editor={editor} iconSize={20} />
              <DividerButton editor={editor} iconSize={20} />
              <LinkButton editor={editor} iconSize={20} />
              <UndoButton editor={editor} iconSize={20} />
              <RedoButton editor={editor} iconSize={20} />
            </MobilePopupTools>
          </div>
        </div>
      </div>

      <LinkEditForm editor={editor} />
    </div>
  );
}

function MobilePopupTools({ children }: { children: React.ReactNode }): JSX.Element {
  const { t } = useTranslation();
  const [showMarkdownHelp, setShowMarkdownHelp] = React.useState(false);
  const helpTitleId = React.useId();
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (showMarkdownHelp) contentRef.current?.focus();
  }, [showMarkdownHelp]);

  return (
    <Popover.Root
      onOpenChange={(open) => {
        if (!open) setShowMarkdownHelp(false);
      }}
    >
      <Popover.Trigger className="mr-2" aria-label={t("More formatting options")}>
        <IconChevronDown size={20} />
      </Popover.Trigger>
      <Popover.Content
        ref={contentRef}
        aria-labelledby={showMarkdownHelp ? helpTitleId : undefined}
        align="end"
        collisionPadding={8}
        className={
          showMarkdownHelp
            ? markdownHelpPopoverClassName
            : "z-10 p-2 max-w-[calc(100vw-1rem)] bg-surface-base rounded-lg shadow-lg border border-stroke-base"
        }
      >
        {showMarkdownHelp ? (
          <MarkdownHelpContent titleId={helpTitleId} />
        ) : (
          <>
            <div className="flex flex-wrap gap-1">{children}</div>
            <div className="mt-2 border-t border-stroke-base pt-2">
              <ToolbarButton
                title={t("Markdown shortcuts")}
                aria-label={t("Markdown shortcuts")}
                testId="toolbar-button-markdown-shortcuts"
                tabIndex={0}
                className="flex w-full items-center gap-2 text-left"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setShowMarkdownHelp(true)}
              >
                <IconKeyboard size={20} className="shrink-0" />
                {t("Markdown shortcuts")}
              </ToolbarButton>
            </div>
          </>
        )}
      </Popover.Content>
    </Popover.Root>
  );
}

function Separator(): React.ReactElement {
  return <div className="border-l border-stroke-base h-4 mx-2" />;
}
